import { applyPermissionsRules } from '@capturi/core'
import {
  Box,
  Flex,
  HStack,
  Icon,
  Switch,
  Text,
  Tooltip,
  VStack,
  chakra,
} from '@chakra-ui/react'
import React, { ReactElement } from 'react'

import { useFeatureFlags } from '@capturi/feature-flags'
import { MdBolt } from 'react-icons/md'
import { getCrudPermissions } from './PermissionsCrud'
import { getOtherPermissions } from './PermissionsOther'
import { getQAPermissions } from './PermissionsQA'
import {
  UiRole,
  UserPermissions,
  defaultPermissionsPerRole,
} from './permissionsUtils'

type Props = {
  permissions: UserPermissions
  setPermissions: React.Dispatch<React.SetStateAction<UserPermissions>>
  inProgress: boolean
  role: UiRole
}

type PermissionRow = {
  key: keyof UserPermissions
  name: string
  description: string
  tooltip?: string
  disabled?: boolean
  stonlyTag?: string
}

const Permissions: React.FC<Props> = ({
  setPermissions,
  permissions,
  inProgress,
  role,
}) => {
  const { isArchiveOrg, enableScoring, enableQualityAssurance } =
    useFeatureFlags()
  const crudPermissions = getCrudPermissions()

  const otherPermissions = getOtherPermissions()

  const qaPermissions = getQAPermissions({
    enableQualityAssurance,
  })

  const hideQualityAssurance = !enableQualityAssurance && !enableScoring

  const togglePermission = (
    key: keyof UserPermissions,
    value: boolean,
  ): void => {
    const userPermissions = applyPermissionsRules(permissions, key, value)
    setPermissions(userPermissions)
  }

  const renderRow = ({
    key,
    name,
    description,
    disabled,
    tooltip,
    stonlyTag,
  }: PermissionRow): ReactElement => {
    return (
      <HStack key={key} align="start">
        <Box mb={2}>
          <chakra.span ml={1}>
            <Switch
              size="sm"
              isDisabled={inProgress || disabled}
              onChange={(e) => togglePermission(key, e.currentTarget.checked)}
              isChecked={permissions[key]}
            />
          </chakra.span>
        </Box>
        <Box mb={2}>
          <Flex alignItems="center" gap={1}>
            <Text lineHeight="1.2">{name}</Text>
            {tooltip && disabled && (
              <Tooltip label={tooltip} placement="top" hasArrow>
                <Box display="inline-flex" alignItems="center" ml={2}>
                  <Icon
                    as={MdBolt}
                    borderRadius="base"
                    boxSize="18px"
                    background="primary.light"
                    color="primary.dark"
                    data-stonly={stonlyTag}
                  />
                </Box>
              </Tooltip>
            )}
          </Flex>
          <Text fontSize="xs" color="gray.600" lineHeight="1.5">
            {description}
          </Text>
        </Box>
      </HStack>
    )
  }

  const renderHeader = (header: string, text: string): ReactElement => {
    return (
      <Box py="4">
        <Flex>
          <Text fontWeight="semibold" fontSize="md">
            {header}
          </Text>
        </Flex>
        <Text>{text}</Text>
      </Box>
    )
  }

  const allowedToChangeEditEverything =
    defaultPermissionsPerRole[role]?.editEverything == null
  return (
    <div>
      {role === 'owner' && (
        <Flex
          alignItems="start"
          justifyContent="space-between"
          data-stonly="editEverythingPermission"
          backgroundColor="greys.50"
          borderRadius="4px"
          px={2}
          border="1px solid"
          borderColor="greys.100"
          mt={4}
          mb={1}
        >
          {renderHeader(
            'Edit everything ⭐',
            'Edit rights to all functionalities in Capturi.',
          )}
          <chakra.span ml={1}>
            <Switch
              py={2}
              isDisabled={inProgress || allowedToChangeEditEverything}
              onChange={(e) =>
                togglePermission('editEverything', e.currentTarget.checked)
              }
              isChecked={permissions.editEverything}
            />
          </chakra.span>
        </Flex>
      )}
      <VStack align="start" gap={4}>
        {!isArchiveOrg && (
          <Box>
            <Text fontSize="sm" fontWeight="bold" mb={2}>
              Create, edit and delete
            </Text>
            {crudPermissions.map(renderRow)}
          </Box>
        )}
        {!hideQualityAssurance && (
          <Box>
            <Text fontSize="sm" fontWeight="bold" mb={2}>
              Quality Assurance
            </Text>

            {qaPermissions.map(renderRow)}
          </Box>
        )}
        <Box>
          <Text fontSize="sm" fontWeight="bold" mb={2}>
            Other
          </Text>

          {otherPermissions.map(renderRow)}
        </Box>
      </VStack>
    </div>
  )
}

export default Permissions
